<template>
  <v-dialog
    v-model="localShow"
    max-width="900px"
    scrollable
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card class="incident-details-dialog">
      <!-- Header del diálogo -->
      <v-card-title class="d-flex align-center pa-4">
        <!-- P13-V7B (refutation): no priority is defaulted to MEDIUM any more; an absent one falls to
             the neutral icon and colour. -->
        <v-icon
          :icon="getPriorityIcon(incident?.priority || '')"
          :color="getPriorityColor(incident?.priority || '')"
          size="28"
          class="mr-3"
        ></v-icon>
        <div class="flex-grow-1">
          <h2 class="text-h6 mb-1">
            Detalles de Incidencia #{{ incident?.id }}
          </h2>
          <p class="text-caption text-grey mb-0">
            {{ incident?.title }}
          </p>
        </div>
        <!-- P13-V7B (refutation): a missing status was announced as "Pendiente". It is not shown. -->
        <v-chip
          v-if="incident?.status"
          :color="getStatusColor(incident.status)"
          variant="tonal"
          class="mr-3"
          data-testid="incident-details-status"
        >
          <v-icon
            :icon="getStatusIcon(incident.status)"
            size="16"
            class="mr-1"
          ></v-icon>
          {{ getStatusText(incident.status) }}
        </v-chip>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="handleClose"
        ></v-btn>
      </v-card-title>

      <!-- Contenido del diálogo -->
      <v-card-text class="pa-0">
        <v-container class="py-6">
          <v-row>
            <!-- Información principal -->
            <v-col cols="12" md="8">
              <!-- Descripción -->
              <v-card flat class="mb-4" elevation="1">
                <v-card-title class="text-h6 pb-2">
                  <v-icon icon="mdi-text-long" class="mr-2"></v-icon>
                  Descripción
                </v-card-title>
                <v-card-text>
                  <p class="incident-description">{{ incident?.description }}</p>
                </v-card-text>
              </v-card>

              <!-- Ubicación (si existe) -->
              <v-card v-if="incident?.location" flat class="mb-4" elevation="1">
                <v-card-title class="text-h6 pb-2">
                  <v-icon icon="mdi-map-marker" class="mr-2"></v-icon>
                  Ubicación
                </v-card-title>
                <v-card-text>
                  <div class="location-info">
                    <div class="coordinates">
                      <strong>Coordenadas:</strong>
                      {{ incident.location.lat.toFixed(6) }}, {{ incident.location.lng.toFixed(6) }}
                    </div>
                    <!-- P13-V7B: the "Ver en Mapa" button is RETIRED. Its handler logged the id to the
                         console and did nothing else; no map navigation ever existed behind it. -->
                  </div>
                </v-card-text>
              </v-card>

              <!-- Comentarios -->
              <v-card flat class="mb-4" elevation="1">
                <v-card-title class="text-h6 pb-2 d-flex justify-space-between align-center">
                  <div>
                    <v-icon icon="mdi-comment-multiple" class="mr-2"></v-icon>
                    Comentarios ({{ comments.length }})
                  </div>
                  <v-btn
                    v-if="canComment"
                    color="primary"
                    variant="outlined"
                    size="small"
                    prepend-icon="mdi-plus"
                    data-testid="incident-details-add-comment"
                    @click="$emit('comment', incident)"
                  >
                    Agregar
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <div v-if="comments.length === 0" class="text-center text-grey py-4">
                    No hay comentarios aún
                  </div>
                  <div v-else class="comments-list">
                    <div
                      v-for="comment in comments"
                      :key="comment.id"
                      class="comment-item"
                    >
                      <div class="comment-header">
                        <strong>{{ comment.userName }}</strong>
                        <span class="comment-time">{{ formatDate(comment.timestamp) }}</span>
                      </div>
                      <div class="comment-text">{{ comment.comment }}</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Historial de estados -->
              <v-card flat elevation="1">
                <v-card-title class="text-h6 pb-2 d-flex justify-space-between align-center">
                  <div>
                    <v-icon icon="mdi-history" class="mr-2"></v-icon>
                    Historial de Estados
                  </div>
                  <v-btn
                    v-if="canViewHistory"
                    variant="text"
                    size="small"
                    prepend-icon="mdi-open-in-new"
                    data-testid="incident-details-full-history"
                    @click="viewFullHistory"
                  >
                    Ver Completo
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-timeline density="compact" side="end">
                    <v-timeline-item
                      v-for="(status, index) in statusHistory"
                      :key="index"
                      :dot-color="getStatusColor(status.status)"
                      size="small"
                    >
                      <div class="timeline-content">
                        <div class="timeline-title">
                          {{ getStatusText(status.status) }}
                        </div>
                        <div class="timeline-subtitle">
                          {{ status.userName }} - {{ formatDate(status.timestamp) }}
                        </div>
                        <div v-if="status.comment" class="timeline-comment">
                          "{{ status.comment }}"
                        </div>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Sidebar derecho con información adicional -->
            <v-col cols="12" md="4">
              <!-- Información básica -->
              <v-card flat class="mb-4" elevation="1">
                <v-card-title class="text-h6 pb-2">
                  <v-icon icon="mdi-information" class="mr-2"></v-icon>
                  Información General
                </v-card-title>
                <v-card-text>
                  <div class="info-list">
                    <!--
                      P13-V7B (refutation): a missing priority printed "Media" and a missing category
                      printed "Otra" — `incident?.priority || 'MEDIUM'`, `incident?.category || 'OTHER'`.
                      An incidence has no category column at all, so every detail claimed "Otra". A
                      field the row does not carry is now said to be absent.
                    -->
                    <div class="info-item">
                      <span class="info-label">Prioridad:</span>
                      <v-chip
                        v-if="incident?.priority"
                        :color="getPriorityColor(incident.priority)"
                        size="small"
                        variant="tonal"
                        data-testid="incident-details-priority"
                      >
                        <v-icon
                          :icon="getPriorityIcon(incident.priority)"
                          size="14"
                          class="mr-1"
                        ></v-icon>
                        {{ getPriorityText(incident.priority) }}
                      </v-chip>
                      <span v-else class="info-value text-grey" data-testid="incident-details-priority-absent">Sin prioridad</span>
                    </div>

                    <div class="info-item">
                      <span class="info-label">Categoría:</span>
                      <v-chip
                        v-if="incident?.category"
                        :color="getCategoryColor(incident.category)"
                        size="small"
                        variant="tonal"
                        data-testid="incident-details-category"
                      >
                        {{ getCategoryText(incident.category) }}
                      </v-chip>
                      <span v-else class="info-value text-grey" data-testid="incident-details-category-absent">Sin categoría</span>
                    </div>
                    
                    <div class="info-item">
                      <span class="info-label">Creada:</span>
                      <span class="info-value">{{ formatDate(incident?.createdAt) }}</span>
                    </div>
                    
                    <div class="info-item">
                      <span class="info-label">Actualizada:</span>
                      <span class="info-value">{{ formatDate(incident?.updatedAt) }}</span>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Asignación -->
              <v-card flat class="mb-4" elevation="1">
                <v-card-title class="text-h6 pb-2 d-flex justify-space-between align-center">
                  <div>
                    <v-icon icon="mdi-account-circle" class="mr-2"></v-icon>
                    Asignación
                  </div>
                  <v-btn
                    v-if="canAssign"
                    color="primary"
                    variant="text"
                    size="small"
                    icon="mdi-pencil"
                    data-testid="incident-details-assign-edit"
                    @click="$emit('assign', incident)"
                  ></v-btn>
                </v-card-title>
                <v-card-text>
                  <!-- P13-V7B: only what the row states is shown. The panel used to look the assignee
                       up by comparing an id against the assignee OBJECT, never matched, and printed
                       "Supervisor no disponible / Zona: N/A / N/A / N/A" for every assigned incident. -->
                  <div v-if="assignedSupervisor" class="assignment-info" data-testid="incident-details-assignee">
                    <div class="supervisor-info">
                      <v-avatar size="40" class="mb-2">
                        <v-img
                          v-if="assignedSupervisor.avatar"
                          :src="assignedSupervisor.avatar"
                          :alt="assignedSupervisor.name || ''"
                        ></v-img>
                        <v-icon v-else icon="mdi-account" size="24"></v-icon>
                      </v-avatar>
                      <div>
                        <div class="supervisor-name">{{ assignedSupervisor.name || 'Asignada' }}</div>
                        <div v-if="assignedSupervisor.zone" class="supervisor-zone">Zona: {{ assignedSupervisor.zone }}</div>
                        <div v-if="assignedSupervisor.email" class="supervisor-contact">
                          <v-icon icon="mdi-email" size="14" class="mr-1"></v-icon>
                          {{ assignedSupervisor.email }}
                        </div>
                        <div v-if="assignedSupervisor.phone" class="supervisor-contact">
                          <v-icon icon="mdi-phone" size="14" class="mr-1"></v-icon>
                          {{ assignedSupervisor.phone }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center text-grey py-3">
                    <v-icon icon="mdi-account-off" size="32" class="mb-2"></v-icon>
                    <div>No asignada</div>
                    <v-btn
                      v-if="canAssign"
                      color="primary"
                      variant="outlined"
                      size="small"
                      class="mt-2"
                      data-testid="incident-details-assign-empty"
                      @click="$emit('assign', incident)"
                    >
                      {{ assignButtonText }}
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Agencia afectada -->
              <v-card flat class="mb-4" elevation="1">
                <v-card-title class="text-h6 pb-2">
                  <v-icon icon="mdi-store" class="mr-2"></v-icon>
                  Agencia Afectada
                </v-card-title>
                <v-card-text>
                  <div v-if="affectedAgency" class="agency-info">
                    <div class="agency-name">
                      {{ affectedAgency.name }}
                    </div>
                    <div v-if="affectedAgency.address" class="agency-address">
                      <v-icon icon="mdi-map-marker" size="small" class="mr-1"></v-icon>
                      {{ affectedAgency.address }}
                    </div>
                    <div v-if="affectedAgency.city" class="agency-city text-grey-darken-1">
                      <v-icon icon="mdi-city" size="small" class="mr-1"></v-icon>
                      {{ affectedAgency.city }}
                    </div>
                  </div>
                  <div v-else class="text-grey">
                    No especificada
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <!-- Acciones del diálogo -->
      <v-card-actions class="dialog-actions pa-6 pt-0">
        <v-btn
          variant="outlined"
          v-if="canUseLifecycleWrites"
          prepend-icon="mdi-pencil"
          @click="$emit('edit', incident)"
        >
          Editar
        </v-btn>
        
        <v-btn
          v-if="canAssign && !incident?.assignedTo"
          color="primary"
          variant="outlined"
          prepend-icon="mdi-account-plus"
          data-testid="incident-details-assign"
          @click="$emit('assign', incident)"
        >
          Asignar
        </v-btn>

        <v-menu v-if="canChangeStatus">
          <template v-slot:activator="{ props }">
            <v-btn
              color="info"
              variant="outlined"
              prepend-icon="mdi-swap-horizontal"
              data-testid="incident-details-change-status"
              v-bind="props"
            >
              Cambiar Estado
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="status in availableStatuses"
              :key="status.value"
              @click="$emit('status-change', status.value)"
            >
              <template v-slot:prepend>
                <v-icon
                  :icon="getStatusIcon(status.value)"
                  :color="getStatusColor(status.value)"
                  size="20"
                ></v-icon>
              </template>
              <v-list-item-title>{{ status.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        
        <v-spacer></v-spacer>
        
        <v-btn
          variant="text"
          @click="handleClose"
        >
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import websocketService from '@/services/websocket.service'
import { RT_LOCAL } from '@/services/realtime-events'
// P13-V6B@V1 — la historia visible proviene SIEMPRE del read-back canónico persistido.
import { incidentsService } from '../../../services/incidentsService'
import { getApiOriginUrl } from '@/config/runtimeConfig'
import type { Incident, Supervisor, Agency, StatusHistory, Comment } from '../../../types/incidents'

interface Props {
  modelValue: boolean
  incident?: Incident | null
  supervisors?: Supervisor[]
  agencies?: Agency[]
  /** W2 LIFECYCLE-SIBLING: the dialog's own "Editar" action. Defaults closed. */
  canUseLifecycleWrites?: boolean
  /**
   * P13-V7B — the four remaining actions are CAPABILITIES THE CONSUMER DECLARES, and each defaults
   * closed. The dialog used to render "Agregar", "Asignar" (three times), "Cambiar Estado" and
   * "Ver Completo" unconditionally and simply emit: whether anything happened depended on a parent
   * it could not see. The field-supervisor map mounts this dialog with handlers that only logged to
   * the console, so that role was offered five actions that did nothing. A consumer that really
   * performs an action says so; one that says nothing gets a read-only detail.
   */
  canComment?: boolean
  canAssign?: boolean
  canChangeStatus?: boolean
  canViewHistory?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  supervisors: () => [],
  agencies: () => [],
  canUseLifecycleWrites: false,
  canComment: false,
  canAssign: false,
  canChangeStatus: false,
  canViewHistory: false
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  edit: [incident: Incident]
  assign: [incident: Incident]
  comment: [incident: Incident]
  'status-change': [newStatus: string]
  'view-history': [incident: Incident]
  'incident-updated': [data: any]
  close: []
}>()

// Auth store para detección de rol
const auth = useAuthStore()

// Estado local
const comments = ref<Comment[]>([])
const statusHistory = ref<StatusHistory[]>([])
const loadingComments = ref(false)

// Computed para textos adaptados por rol
const isDepartmentHead = computed(() => auth.userRole === 'department_head')

const assignButtonText = computed(() => 
  isDepartmentHead.value ? 'Asignar a Miembro' : 'Asignar Supervisor'
)

// Computed
const localShow = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

/**
 * P13-V7B — the assignee as the ROW states it. `assignedTo` is the assignee itself (`{ id, name }`)
 * on the readers of this dialog; the catalogue passed by the consumer only ENRICHES it (zone,
 * contact) when it knows that person. Nothing is defaulted: an unknown field is absent, and the
 * template renders only the fields that exist.
 */
const assignedSupervisor = computed(() => {
  const assigned: any = props.incident?.assignedTo
  if (!assigned) return null

  const assignedId = typeof assigned === 'object' ? assigned.id : assigned
  const known = props.supervisors.find(s => String(s.id) === String(assignedId))

  return {
    id: assignedId,
    name: (typeof assigned === 'object' ? assigned.name : null) || known?.name || null,
    email: known?.email || null,
    phone: known?.phone || null,
    zone: known?.zone || null,
    avatar: (typeof assigned === 'object' ? assigned.avatar : null) || known?.avatar || null
  }
})

const affectedAgency = computed(() => {
  // Primero intentar usar el objeto point directamente si viene del backend
  if (props.incident?.point) {
    return {
      id: props.incident.point.id,
      name: props.incident.point.name,
      address: props.incident.point.address || null,
      city: props.incident.point.city
    } as any
  }
  
  // Fallback: buscar por agencyId en la lista de agencies
  if (props.incident?.agencyId && props.agencies) {
    const agency = props.agencies.find(a => Number(a.id) === props.incident?.agencyId)
    if (agency) {
      return agency
    }
  }
  
  return null
})

// P13-V1D (frozen decision #1): the five canonical states — the retired 'RESOLVED' was still a
// WRITABLE target here (it produced a legacy {status:'resolved'} write the server now fails
// closed). The server's FSM remains the only authority over which of these transitions succeed.
const availableStatuses = [
  { title: 'Pendiente', value: 'PENDING' },
  { title: 'En Proceso', value: 'IN_PROGRESS' },
  { title: 'Pendiente Validación', value: 'PENDING_VALIDATION' },
  { title: 'Cerrado', value: 'CLOSED' },
  { title: 'Cancelado', value: 'CANCELLED' }
]

// Métodos
const handleClose = () => {
  localShow.value = false
  emit('close')
}

const viewFullHistory = () => {
  if (props.incident) {
    emit('view-history', props.incident)
  }
}

const loadIncidentDetails = async () => {
  if (!props.incident) return

  // Cargar comentarios desde el backend
  await loadComments()

  // P13-V6B@V1 HISTORY_TRUTH = CANONICAL_READBACK — el historial visible es EXACTAMENTE el
  // persistido (`incident_status_history`) devuelto por el backend. Cero entradas sintéticas:
  // un historial vacío se muestra vacío (nunca se fabrica un actor 'Sistema' ni ids locales).
  try {
    const fresh = await incidentsService.getIncidentById(props.incident.id, { cacheBuster: Date.now() })
    statusHistory.value = fresh.statusHistory || []
  } catch (error) {
    console.error('❌ Error cargando historial persistido:', error)
    statusHistory.value = []
  }
}

const loadComments = async () => {
  if (!props.incident) return
  
  loadingComments.value = true
  
  try {
    const apiUrl = getApiOriginUrl()
    const response = await fetch(
      `${apiUrl}/api/incidences/${props.incident.id}/comments`,
      {
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        }
      }
    )
    
    if (!response.ok) {
      console.error('Error al cargar comentarios:', response.status)
      comments.value = []
      return
    }
    
    const result = await response.json()
    
    if (result.success && Array.isArray(result.data)) {
      comments.value = result.data
      console.log(`✅ Cargados ${result.count} comentarios para vista previa`)
      if (result.data.length > 0) {
        console.log('📊 Primer comentario:', result.data[0])
      }
    } else {
      comments.value = []
    }
  } catch (error) {
    console.error('❌ Error cargando comentarios:', error)
    comments.value = []
  } finally {
    loadingComments.value = false
  }
}

// Métodos auxiliares para formateo
const formatDate = (dateString?: string) => {
  if (!dateString) return 'No disponible'
  
  const date = new Date(dateString)
  
  // Validar que la fecha es válida
  if (isNaN(date.getTime())) {
    console.warn('⚠️ formatDate: fecha inválida:', dateString)
    return 'Fecha inválida'
  }
  
  return date.toLocaleString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getPriorityColor = (priority: string) => {
  const colors: Record<string, string> = {
    CRITICAL: 'error',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'success'
  }
  return colors[priority] || 'grey'
}

const getPriorityIcon = (priority: string) => {
  const icons: Record<string, string> = {
    CRITICAL: 'mdi-alert-circle',
    HIGH: 'mdi-alert',
    MEDIUM: 'mdi-information',
    LOW: 'mdi-check-circle'
  }
  return icons[priority] || 'mdi-help'
}

const getPriorityText = (priority: string) => {
  const texts: Record<string, string> = {
    CRITICAL: 'Crítica',
    HIGH: 'Alta',
    MEDIUM: 'Media',
    LOW: 'Baja'
  }
  return texts[priority] || priority
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    PENDING_VALIDATION: 'purple',
    CLOSED: 'grey',
    CANCELLED: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status: string) => {
  const icons: Record<string, string> = {
    PENDING: 'mdi-clock-outline',
    IN_PROGRESS: 'mdi-progress-clock',
    PENDING_VALIDATION: 'mdi-clipboard-check-outline',
    CLOSED: 'mdi-check-all',
    CANCELLED: 'mdi-close-circle'
  }
  return icons[status] || 'mdi-help'
}

const getStatusText = (status: string) => {
  const texts: Record<string, string> = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En Proceso',
    PENDING_VALIDATION: 'Pendiente Validación',
    CLOSED: 'Cerrado',
    CANCELLED: 'Cancelado'
  }
  return texts[status] || status
}

const getCategoryColor = (category: string) => {
  const colors: Record<string, string> = {
    PERSONAL: 'purple',
    TECHNICAL: 'blue',
    INFRASTRUCTURE: 'orange',
    ELECTRICAL: 'yellow',
    INTERNET: 'green',
    EQUIPMENT: 'teal',
    OTHER: 'grey'
  }
  return colors[category] || 'grey'
}

const getCategoryText = (category: string) => {
  const texts: Record<string, string> = {
    PERSONAL: 'Personal',
    TECHNICAL: 'Técnica',
    INFRASTRUCTURE: 'Infraestructura',
    ELECTRICAL: 'Eléctrica',
    INTERNET: 'Internet',
    EQUIPMENT: 'Equipos',
    OTHER: 'Otra'
  }
  return texts[category] || category
}

// WebSocket listeners para tiempo real.
//
// P13-K4A: delivery is addressed to `user:{id}`, so these events carry every incidence this actor is
// an eligible recipient of — not just the open one. Each handler checks the subject before acting.
const belongsToOpenIncident = (incidentId: unknown): boolean => {
  if (!props.incident) return false
  return Number(incidentId) === Number(props.incident.id)
}

const handleCommentCreated = (data?: { incidentId?: number }) => {
  console.log('📨 Evento comment:created recibido en IncidentDetailsDialog')
  if (!belongsToOpenIncident(data?.incidentId)) return
  loadComments()
}

const handleCommentUpdated = (data?: { incidentId?: number }) => {
  console.log('✏️ Evento comment:updated recibido en IncidentDetailsDialog')
  if (!belongsToOpenIncident(data?.incidentId)) return
  loadComments()
}

const handleCommentDeleted = (data?: { incidentId?: number }) => {
  console.log('🗑️ Evento comment:deleted recibido en IncidentDetailsDialog')
  if (!belongsToOpenIncident(data?.incidentId)) return
  loadComments()
}

const setupWebSocketListeners = () => {
  if (!props.incident) return

  // Recargar comentarios cuando haya cambios
  websocketService.on(RT_LOCAL.COMMENT_CREATED, handleCommentCreated)
  websocketService.on(RT_LOCAL.COMMENT_UPDATED, handleCommentUpdated)
  websocketService.on(RT_LOCAL.COMMENT_DELETED, handleCommentDeleted)
}

const cleanupWebSocketListeners = () => {
  if (!props.incident) return

  // Remover listeners CON referencia de handler
  websocketService.off(RT_LOCAL.COMMENT_CREATED, handleCommentCreated)
  websocketService.off(RT_LOCAL.COMMENT_UPDATED, handleCommentUpdated)
  websocketService.off(RT_LOCAL.COMMENT_DELETED, handleCommentDeleted)
}

// Watchers
watch(() => props.modelValue, (newValue) => {
  if (newValue && props.incident) {
    loadIncidentDetails()
    setupWebSocketListeners()
  } else {
    cleanupWebSocketListeners()
  }
})

// Exponer métodos para el componente padre
defineExpose({
  loadComments
})

// 🔄 Listener de WebSocket para actualizaciones en tiempo real del estado.
// P13-V6B@V1 — el evento es una SEÑAL: la actualización visible proviene de un refetch canónico
// (verdad persistida, actor real incluido). Cero fabricación de historial, cero mutación de props;
// el padre reconcilia su lista/selección con los valores del SERVIDOR emitidos aquí.
const handleStatusChanged = async (data: any) => {
  console.log('📡 IncidentDetailsDialog: Evento incident:status-changed recibido:', data)

  const subjectId = Number(data?.incidenceId)
  if (!props.incident || !Number.isInteger(subjectId) || subjectId <= 0) return
  if (subjectId !== Number(props.incident.id)) return

  try {
    const fresh = await incidentsService.getIncidentById(subjectId, { cacheBuster: Date.now() })
    statusHistory.value = fresh.statusHistory || []
    // Reconciliar al padre con la verdad del servidor (él actualiza lista + selectedIncident,
    // que es la fuente reactiva de este prop — este componente no muta sus props).
    emit('incident-updated', {
      id: fresh.id,
      status: fresh.status,
      updatedAt: fresh.updatedAt,
    })
  } catch (error: any) {
    // 403/404: este actor ya no puede leer la incidencia — no se materializa nada desde el payload.
    console.warn('⚠️ Refetch tras status-changed falló (posible pérdida de acceso):', error?.message)
  }
}

onMounted(() => {
  // Escuchar eventos de cambio de estado
  websocketService.on(RT_LOCAL.INCIDENT_STATUS_CHANGED, handleStatusChanged)
  console.log('👂 IncidentDetailsDialog: Listener WebSocket registrado para cambios de estado')
})

onUnmounted(() => {
  // Limpiar listeners al desmontar
  websocketService.off(RT_LOCAL.INCIDENT_STATUS_CHANGED, handleStatusChanged)
  console.log('🧹 IncidentDetailsDialog: Listener WebSocket removido')
})
</script>

<style scoped>
.incident-details-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 1.5rem 2rem;
}

.dialog-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

.dialog-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

/* Información detallada */
.incident-description {
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
}

.location-info {
  font-size: 0.9rem;
}

.coordinates {
  font-family: monospace;
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

/* Lista de información */
.info-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  font-weight: 500;
  color: #666;
  font-size: 0.875rem;
}

.info-value {
  font-size: 0.875rem;
  color: #333;
}

/* Comentarios */
.comments-list {
  max-height: 300px;
  overflow-y: auto;
}

.comment-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  background: #fafafa;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.comment-time {
  font-size: 0.75rem;
  color: #666;
}

.comment-text {
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Timeline */
.timeline-content {
  padding-left: 1rem;
}

.timeline-title {
  font-weight: 600;
  font-size: 0.9rem;
}

.timeline-subtitle {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
}

.timeline-comment {
  font-size: 0.8rem;
  font-style: italic;
  color: #555;
  margin-top: 0.25rem;
}

/* Asignación */
.assignment-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.supervisor-info {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.supervisor-name {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.supervisor-zone {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.supervisor-contact {
  font-size: 0.8rem;
  color: #555;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
}

/* Agencia */
.agency-info {
  text-align: center;
}

.agency-name {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.agency-address {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.75rem;
}

.agency-status {
  display: flex;
  justify-content: center;
}

/* Responsive */
@media (max-width: 768px) {
  .dialog-header {
    padding: 1rem;
  }
  
  .dialog-title {
    font-size: 1.2rem;
  }
  
  :deep(.v-container) {
    padding: 1rem !important;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}

/* Scrollbar personalizado */
.comments-list::-webkit-scrollbar {
  width: 6px;
}

.comments-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.comments-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.comments-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
